/**
 * The header menu: the sets, the account, the language, and the way out.
 *
 * Every screen inside the app hangs off this, so there is always a way back to
 * the list of sets — from a result, from a review, from the account page.
 *
 * Four entries and no more. The marketing pages — the exam guide, the price
 * list — belong to the reader who has not signed in yet; inside the app they
 * were a menu that answered a question nobody was asking, and the price is
 * carried by `/pass`, which is where the locked sets already lead. `/writing`
 * is reached from a marking, which is the moment its list means anything.
 *
 * Hand-rolled rather than a Radix dropdown for the same reason as the language
 * switcher: the menu is anchored to a header that is `position: sticky`, and
 * the floating-position machinery landed it off-screen there.
 */
import { useEffect, useRef, useState } from "react";
import { BarChart3, ListChecks, LogIn, LogOut, Menu, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/state/session";
import { useT } from "@/i18n";
import { navigate } from "@/lib/router";
import { signOutUser } from "@/lib/firebase";
import { LanguageSwitcher } from "@/components/language-switcher";
import { cn } from "@/lib/utils";

export function AppMenu() {
  const t = useT();
  const session = useSession();
  const signedIn = session.status === "ready";
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // Anything outside the menu — a click or Escape — closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const go = (path: string): void => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="relative" ref={box}>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 px-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.menu")}
        onClick={() => setOpen((was) => !was)}
      >
        <Menu className="size-4" />
        <span className="hidden text-xs sm:inline">{t("nav.menu")}</span>
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-md border bg-popover py-1 shadow-lg"
        >
          <MenuItem icon={<ListChecks className="size-4" />} onClick={() => go("/app")}>
            {t("nav.tests")}
          </MenuItem>
          {/* Signed out, `/account` is the door rather than the account page,
              so one entry covers both. */}
          {signedIn ? (
            <>
              <MenuItem icon={<User className="size-4" />} onClick={() => go("/account")}>
                {t("nav.account")}
              </MenuItem>
              {/* The operator only; one reader, so not a catalogue string. */}
              {session.admin && (
                <MenuItem icon={<BarChart3 className="size-4" />} onClick={() => go("/admin")}>
                  Адмінка
                </MenuItem>
              )}
              <MenuItem
                icon={<LogOut className="size-4" />}
                onClick={() => {
                  setOpen(false);
                  void signOutUser().then(() => navigate("/"));
                }}
              >
                {t("nav.signOut")}
              </MenuItem>
            </>
          ) : (
            <MenuItem icon={<LogIn className="size-4" />} onClick={() => go("/account")}>
              {t("nav.signIn")}
            </MenuItem>
          )}

          {/* The language lives here rather than in the header: it is set once
              and then never touched again, and beside the menu button it was
              taking the width a title needs on a phone. Picking one leaves the
              menu open, so the labels change under the cursor. */}
          <div className="mt-1 flex items-center justify-between gap-2 border-t px-3 pb-1 pt-2">
            <span className="text-xs text-muted-foreground">{t("nav.language")}</span>
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm",
        "hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none",
      )}
    >
      <span className="text-muted-foreground">{icon}</span>
      {children}
    </button>
  );
}
